// Vector stores a tenant has registered. Knowledge bases and the agent semantic
// cache pick one instead of carrying a URL and key of their own — the third
// registry beside LLM providers and embedding models.
//
// The backend refuses a URL change while knowledge bases keep vectors in a
// store (their collections live at the current URL) and a delete while anything
// references it. Both 409 messages are surfaced verbatim.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Plus, XCircle } from "lucide-react";
import * as React from "react";

import { PageHeader } from "@/components/layout-bits";
import { ReturnBanner } from "@/components/return-banner";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from "@/components/ui/primitives";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { toast } from "@/components/ui/toaster";
import {
  createVectorStore,
  deleteVectorStore,
  testVectorStore,
  updateVectorStore,
} from "@/lib/api/endpoints";
import { useIsOrgAdmin } from "@/lib/access";
import { useRouter } from "@/lib/navigation";
import { useVectorStores } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";
import { useReturnTo } from "@/lib/return-to";
import type { VectorStoreUpdateInput, VectorStoreView } from "@/types/api";

function usageLabel(row: VectorStoreView): string {
  const parts: string[] = [];
  if (row.knowledge_base_count)
    parts.push(
      `${row.knowledge_base_count} knowledge base${row.knowledge_base_count === 1 ? "" : "s"}`,
    );
  if (row.agent_count)
    parts.push(`${row.agent_count} agent cache${row.agent_count === 1 ? "" : "s"}`);
  return parts.join(" · ");
}

export default function VectorStoresPage() {
  const returnTo = useReturnTo();
  const router = useRouter();
  const qc = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useVectorStores();
  // Engineers see what is registered (they pick from it); adding, editing,
  // testing and deleting a store is the org admin's, enforced server-side.
  const canManage = useIsOrgAdmin();

  const [creating, setCreating] = React.useState(false);
  const [editing, setEditing] = React.useState<VectorStoreView | null>(null);
  const [pendingDelete, setPendingDelete] = React.useState<VectorStoreView | null>(null);

  const invalidate = () => void qc.invalidateQueries({ queryKey: qk.vectorStores });

  const deleteMutation = useMutation({
    mutationFn: deleteVectorStore,
    onSuccess: () => {
      toast.success("Vector store deleted.");
      setPendingDelete(null);
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const testMutation = useMutation({
    mutationFn: testVectorStore,
    onSuccess: (result) => {
      if (result.ok) toast.success("Connected.");
      else toast.error(result.error ?? "Could not connect.");
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <>
      {returnTo ? <ReturnBanner to={returnTo} noun="a vector store" /> : null}
      <PageHeader
        title="Vector stores"
        description="Vector databases your knowledge bases and agent semantic caches keep their vectors in. Register one here and pick it wherever vectors are needed."
        actions={
          canManage ? (
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" /> Add vector store
            </Button>
          ) : undefined
        }
      />

      {isLoading ? (
        <Skeleton className="h-56 w-full" />
      ) : isError ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : !data?.length ? (
        <EmptyState
          title="No vector stores registered yet"
          hint={
            canManage
              ? "Add one here and select it when creating a knowledge base or turning on an agent's semantic cache — the URL and key are entered once."
              : "Ask an org admin to add one; you can then select it for a knowledge base or an agent's semantic cache."
          }
          action={
            canManage ? (
              <Button onClick={() => setCreating(true)}>Add vector store</Button>
            ) : undefined
          }
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <THead>
                <TR>
                  <TH>Name</TH>
                  <TH>Provider</TH>
                  <TH>URL</TH>
                  <TH>Key</TH>
                  <TH>In use</TH>
                  <TH>Status</TH>
                  <TH />
                </TR>
              </THead>
              <TBody>
                {data.map((row) => (
                  <TR key={row.id}>
                    <TD>
                      <div className="font-medium">{row.name}</div>
                      <code className="text-xs text-muted-foreground">{row.slug}</code>
                    </TD>
                    <TD className="text-sm">{row.provider}</TD>
                    <TD>
                      <code className="break-all text-xs">{row.url}</code>
                    </TD>
                    <TD>
                      <code className="text-xs text-muted-foreground">{row.key_hint ?? "—"}</code>
                    </TD>
                    <TD className="text-sm">
                      {row.used_by_count ? (
                        <Badge variant="secondary">{usageLabel(row)}</Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TD>
                    <TD>
                      {row.last_error ? (
                        <span
                          className="inline-flex items-center gap-1 text-xs text-destructive"
                          title={row.last_error}
                        >
                          <XCircle className="h-3.5 w-3.5" /> failed
                        </span>
                      ) : row.last_verified_at ? (
                        <span className="inline-flex items-center gap-1 text-xs text-[hsl(var(--success))]">
                          <CheckCircle2 className="h-3.5 w-3.5" /> verified
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">not tested</span>
                      )}
                    </TD>
                    <TD className="text-right">
                      {canManage ? (
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={testMutation.isPending}
                            onClick={() => testMutation.mutate(row.slug)}
                          >
                            Test
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditing(row)}>
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setPendingDelete(row)}
                            title={row.used_by_count ? `In use by ${usageLabel(row)}` : undefined}
                          >
                            Delete
                          </Button>
                        </div>
                      ) : null}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <CreateVectorStoreDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={() => {
          invalidate();
          if (returnTo) router.push(returnTo);
        }}
      />

      {editing ? (
        <EditVectorStoreDialog
          store={editing}
          onOpenChange={(open) => !open && setEditing(null)}
          onSaved={invalidate}
        />
      ) : null}

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={`Delete ${pendingDelete?.name ?? "vector store"}?`}
        description={
          pendingDelete?.used_by_count
            ? `This store is used by ${usageLabel(pendingDelete)}. Deleting it will be refused until they point somewhere else.`
            : "Only the connection is deleted — collections inside the database are left as they are."
        }
        confirmLabel="Delete"
        pending={deleteMutation.isPending}
        onConfirm={() => pendingDelete && deleteMutation.mutate(pendingDelete.slug)}
      />
    </>
  );
}

function CreateVectorStoreDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}) {
  const [name, setName] = React.useState("");
  const [url, setUrl] = React.useState("");
  const [apiKey, setApiKey] = React.useState("");

  React.useEffect(() => {
    if (!open) return;
    setName("");
    setUrl("");
    setApiKey("");
  }, [open]);

  const create = useMutation({
    mutationFn: () =>
      createVectorStore({
        name: name.trim(),
        provider: "qdrant",
        url: url.trim(),
        api_key: apiKey.trim() || null,
      }),
    onSuccess: () => {
      toast.success("Vector store added. Test it to confirm the connection.");
      onOpenChange(false);
      onCreated();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const valid = !!name.trim() && !!url.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add a vector store</DialogTitle>
          <DialogDescription>
            Qdrant today. The key is stored encrypted and never shown again.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid && !create.isPending) create.mutate();
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="vs-name">Name</Label>
            <Input
              id="vs-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Production Qdrant"
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="vs-url">URL</Label>
            <Input
              id="vs-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://xyz.eu-central.aws.cloud.qdrant.io"
              className="font-mono text-xs"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="vs-key">API key (optional)</Label>
            <Input
              id="vs-key"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Only for Qdrant Cloud / secured instances"
              autoComplete="off"
              className="font-mono text-xs"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid || create.isPending}>
              {create.isPending ? "Adding…" : "Add vector store"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditVectorStoreDialog({
  store,
  onOpenChange,
  onSaved,
}: {
  store: VectorStoreView;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const [name, setName] = React.useState(store.name);
  const [url, setUrl] = React.useState(store.url);
  const [apiKey, setApiKey] = React.useState("");
  const [clearKey, setClearKey] = React.useState(false);

  // Its collections live at the current URL, so the backend refuses to move it.
  const urlLocked = store.knowledge_base_count > 0;

  const save = useMutation({
    mutationFn: () => {
      const body: VectorStoreUpdateInput = {};
      if (name.trim() && name.trim() !== store.name) body.name = name.trim();
      if (!urlLocked && url.trim() && url.trim() !== store.url) body.url = url.trim();
      if (clearKey) body.api_key = "";
      else if (apiKey.trim()) body.api_key = apiKey.trim();
      return updateVectorStore(store.slug, body);
    },
    onSuccess: () => {
      toast.success("Vector store updated.");
      onSaved();
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit “{store.name}”</DialogTitle>
          <DialogDescription>Leave the key blank to keep the current one.</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!save.isPending) save.mutate();
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="vse-name">Name</Label>
            <Input id="vse-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="vse-url">URL</Label>
            <Input
              id="vse-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={urlLocked}
              className="font-mono text-xs"
            />
            {urlLocked ? (
              <p className="text-xs text-muted-foreground">
                Locked: {store.knowledge_base_count} knowledge base
                {store.knowledge_base_count === 1 ? " keeps its" : "s keep their"} vectors here.
                Register a new store and re-point them instead.
              </p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="vse-key">API key</Label>
            <Input
              id="vse-key"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={
                store.key_hint ? `${store.key_hint} — enter a new value to rotate` : "None"
              }
              autoComplete="off"
              disabled={clearKey}
              className="font-mono text-xs"
            />
            {store.key_hint ? (
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 rounded border-input accent-primary"
                  checked={clearKey}
                  onChange={(e) => setClearKey(e.target.checked)}
                />
                Remove the stored key
              </label>
            ) : null}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || save.isPending}>
              {save.isPending ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
